'use client';
import { useLanguage } from '@bells/core/i18n';
import { parseErrorSchema } from '@bells/core/lib/zod';
import { generateClient } from '@bells/core/rest/client';
import { type AllPossibleRestErrors } from '@bells/schema/models/data';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type AppRouteMutation,
  type ContractAnyType,
  type ServerInferResponses,
  type ClientInferResponses,
} from '@ts-rest/core';
import { useEffect, useId, useMemo, useState } from 'react';
import { useForm, type FieldError, type UseFormProps } from 'react-hook-form';
import { z, ZodType } from 'zod';
import type { UseZodForm } from '../components/form-root';
import { toast } from '../utils/use-toast';

export type ResponseCodes = 200 | 201 | 202 | 203;

type FilterStatus<T, U> = T extends { status: U } ? never : T;
type FilterStatusInclude<T, U> = T extends { status: U } ? T : never;

type ZodConvert<T = unknown> = T extends ZodType ? z.infer<T> : T;

export type ValidationType<
  TRouter extends AppRouteMutation,
  TSchema extends z.ZodType,
> = Omit<UseFormProps<z.input<TSchema>>, 'resolver'> & {
  schema: TSchema;
  router: TRouter;
  onSuccess?: (
    val: FilterStatusInclude<ClientInferResponses<TRouter>, ResponseCodes>,
    form: UseZodForm<z.input<TSchema>>
  ) => void;

  onError?: (
    val: FilterStatus<ServerInferResponses<TRouter>, ResponseCodes>,
    form: UseZodForm<TSchema['_input']>
  ) => void;
  prepare?: (val: z.input<TSchema>) => any;
} & ([TRouter['pathParams']] extends [ContractAnyType | undefined]
    ? Required<{ pathParams: ZodConvert<TRouter['pathParams']> }>
    : Partial<{ pathParams: never }>) &
  ([TRouter['query']] extends [ContractAnyType | undefined]
    ? Partial<{ query: ZodConvert<TRouter['query']> }>
    : Partial<{ query: never }>);

export const useValidationForm = <
  TRouter extends AppRouteMutation,
  TSchema extends z.ZodType,
>({
  schema,
  router,
  pathParams,
  query,
  ...props
}: ValidationType<TRouter, TSchema>) => {
  const systemRestClient = useMemo(() => {
    return generateClient({ save: router });
  }, [router]);

  const { language } = useLanguage();

  const [rateLimit, setRateLimit] = useState<number>(0);

  const form = useForm<z.input<TSchema>>({
    ...props,
    resolver: zodResolver(schema, undefined, { raw: true }),
  }) as UseZodForm<z.input<TSchema>>;

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setRateLimit((prevTime) => {
        return Math.max(0, prevTime - 1000);
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [rateLimit]);

  form.id = useId();

  const submit = form.handleSubmit(async (inputValues) => {
    if (rateLimit > 0) {
      return;
    }

    const dataInput = props.prepare ? props.prepare(inputValues) : inputValues;

    const dataParams = {
      body: dataInput,
      params: pathParams,
      query,
      extraHeaders: { 'x-language': language.toString() },
    };

    const func = systemRestClient.save;

    const data = await (func as any)(dataParams);

    if (data.status == 201 || data.status == 200 || data.status == 202) {
      //form.reset();
      if (props.onSuccess) {
        props.onSuccess(data as any, form);
      }
    } else {
      if (props.onError) {
        props.onError(data as any, form);
      }
      const error: AllPossibleRestErrors = data.body;

      if (error.type === 'server') {
        form.setError('root.server', error);

        if (error.message) {
          toast.error('Uppps...', {
            description: error.message,
          });
        }
      }
      if (error.type === 'zod') {
        const errors = parseErrorSchema(error?.issues ?? [], true);
        form.control._setErrors(errors as any);
      }

      if (error.type === 'rate-limit') {
        const nextTime = error.timeToExpire * 1000;
        setRateLimit(nextTime);
      }

      if (error.type === 'field') {
        const fields = error.fields ?? {};
        const dictionary: { [key: string]: FieldError } = Object.fromEntries(
          Object.keys(fields).map((item) => [
            item,
            { message: fields[item], type: 'validate' },
          ])
        );

        form.control._setErrors(dictionary as any);
      }
    }
  });

  form.startQuery = submit;
  form.doSubmit = () => {
    submit();
  };

  form.rateLimit = rateLimit;
  return form;
};
